import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import {
  getSdkErrorKey,
  isAuth0MessageInLanguage,
} from "@/utils/helpers/errorUtils";

type SdkErrorLike = { code?: string; message?: string } | undefined;

const getAuth0Locale = (): string | undefined =>
  typeof window === "undefined"
    ? undefined
    : window.universal_login_context?.transaction?.locale;

/**
 * Returns a function that resolves an Auth0 SDK error to text in the active UI language.
 * - Known error code → our translation.
 * - Unknown code, Auth0 locale matches the UI language → Auth0's own message.
 * - Unknown code, languages differ → undefined, so the caller shows its translated fallback.
 */
export function useSdkErrorMessage(): (
  error: SdkErrorLike
) => string | undefined {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  return useCallback(
    (error: SdkErrorLike) => {
      if (!error) return undefined;

      const key = getSdkErrorKey(error.code);
      if (key) return t(key);

      if (isAuth0MessageInLanguage(getAuth0Locale(), language)) {
        return error.message || undefined;
      }
      return undefined;
    },
    [t, language]
  );
}
